/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2019 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import { defineMessages } from '@nebula/izzy/intl';
import { showError, showInfo, showSuccess } from '@nebula/corona-toast-plugin/dist/actions';

const MSG = defineMessages({
  accessChanges: {
    id: 'gemini.access.changes',
    defaultMessage: 'Project Griffon will be generally available, as Assurance, to all'
    + ' Adobe Experience Cloud customers in October 2022. At that time, access to Assurance'
    + ' will be managed through the Admin Console - which is the central location to manage'
    + ' Adobe entitlements across your entire organization. Adobe administrators will be able'
    + ' to administer and manage user access to Assurance. To ensure uninterrupted access,'
    + ' contact your Adobe Administrator immediately and request access to Adobe Experience'
    + ' Platform from the Admin Console'
  },
  clientConnected: {
    id: 'gemini.session.client.connected',
    defaultMessage: 'Client connected'
  },
  clientDisconnected: {
    id: 'gemini.session.client.disconnected',
    defaultMessage: 'Client disconnected'
  },
  doradoSessionsListQueryError: {
    id: 'gemini.dorado.sessions.list.query.error',
    defaultMessage: 'There was a problem loading the sessions'
  },
  doradoSessionsViewQueryError: {
    id: 'gemini.dorado.session.view.query.error',
    defaultMessage: 'There was a problem loading the data for this session'
  },
  sessionExpiring: {
    id: 'gemini.session.expiring',
    defaultMessage: 'This session will be deleted soon. Sessions are deleted '
    + 'after 30 days. See documentation for more information.'
  },
  sessionSaveDuplicateError: {
    id: 'gemini.session.create.error.duplicate',
    defaultMessage: 'The session name already exists'
  },
  sessionSaveError: {
    id: 'gemini.session.create.error',
    defaultMessage: 'There was a problem saving the session'
  },
  sessionSaveSuccess: {
    id: 'gemini.session.create.success',
    defaultMessage: 'The session was saved successfully'
  },
  sessionsDeleteErrorSingle: {
    id: 'gemini.session.delete.error.single',
    defaultMessage: 'There was a problem deleting the session'
  },
  sessionsDeleteErrorMultiple: {
    id: 'gemini.session.delete.error.multiple',
    defaultMessage: 'There was a problem deleting one or more of the sessions'
  },
  sessionsDeleteSuccessSingle: {
    id: 'gemini.session.delete.success.single',
    defaultMessage: 'The session was deleted successfully'
  },
  sessionsDeleteSuccessMultiple: {
    id: 'gemini.session.delete.success.multiple',
    defaultMessage: 'The sessions were deleted successfully'
  },
  sendCommandSuccess: {
    id: 'gemini.command.send.success',
    defaultMessage: 'The command was successfully sent'
  },
  sendCommandError: {
    id: 'gemini.command.send.error',
    defaultMessage: 'The command failed to send'
  }
});

const info = msg => showInfo(msg, { autohide: false, closable: true, localized: true });
const success = msg => showSuccess(msg, { localized: true });
const error = msg => showError(msg, { localized: true });

export const accessChanges = info(MSG.accessChanges);

export const clientConnected = success(MSG.clientConnected);
export const clientDisconnected = error(MSG.clientDisconnected);

export const doradoSessionsListQueryError = error(MSG.doradoSessionsListQueryError);
export const doradoSessionsViewQueryError = error(MSG.doradoSessionsViewQueryError);

export const sessionExpiring = info(MSG.sessionExpiring);

export const sessionSaveSuccess = success(MSG.sessionSaveSuccess);
export const sessionDeleteSuccessSingle = success(MSG.sessionsDeleteSuccessSingle);
export const sessionDeleteSuccessMultiple = success(MSG.sessionsDeleteSuccessMultiple);
export const sendCommandSuccess = success(MSG.sendCommandSuccess);

export const sessionSaveDuplicateError = error(MSG.sessionSaveDuplicateError);
export const sessionSaveError = error(MSG.sessionSaveError);
export const sessionDeleteErrorSingle = error(MSG.sessionsDeleteErrorSingle);
export const sessionDeleteErrorMultiple = error(MSG.sessionsDeleteErrorMultiple);
export const sendCommandError = error(MSG.sendCommandError);
